import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, ListItemIcon, Radio, TextField } from "@outpave/ds";
import {
  Button,
  FormControlLabel,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { HugeiconsIcon } from "@hugeicons/react";
import { Invoice03Icon, PolicyIcon } from "@hugeicons-pro/core-stroke-rounded";
import transactionPolicies from "../../../validations/transactionPolicies";
import SettingPolicyType from "../../../enums/SettingPolicyType";
import { TransactionPolicy } from "hooks/settingPolicy.types";
import { getCurrencySymbol } from "utils/formatCash";
import { Inline, Stack } from "components/layout/reusableLayouts";

interface TransactionPoliciesDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
  policy: TransactionPolicy | null;
  onUpdateCategoriesAmount: (isAllCategories: boolean, amount: string) => void;
  isAllCategories: boolean;
}

// Registered here as a module side effect that other admin modules rely on:
// calculateDefaultStripeExpirationDate calls .utc() and never extends dayjs
// itself. Do not remove these imports as "unused".
dayjs.extend(utc);

const TransactionPoliciesDrawer: React.FC<TransactionPoliciesDrawerProps> = (
  props,
) => {
  const formik = useFormik({
    initialValues: {
      transactionsAmount: "",
      isAllCategories: props.isAllCategories ? "1" : "0",
    },
    validationSchema: transactionPolicies,
    onSubmit: (values) => {
      props.onUpdateCategoriesAmount(
        values.isAllCategories === "1",
        values.transactionsAmount,
      );
    },
  });

  const { open, handleCloseDrawer } = props;
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      formik.resetForm();
      formik.setFieldValue(
        "isAllCategories",
        props.isAllCategories ? "1" : "0",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const drawerDescription = useMemo(() => {
    switch (props.policy?.type) {
      case SettingPolicyType.TRANSACTION_RECEIPT:
        return t("Receipt Policy");
      case SettingPolicyType.TRANSACTION_COST_ALLOCATION:
        return t("Allocation Policy");
      default:
        return "";
    }
  }, [props.policy]);

  const isReceiptPolicy =
    props.policy?.type === SettingPolicyType.TRANSACTION_RECEIPT;

  // The bulk amount lands on this policy's own categories, so it is their
  // currency that belongs in the field, not a hardcoded dollar.
  const currencySymbol = getCurrencySymbol(
    props.policy?.policy?.categories?.[0]?.currency ?? "USD",
  );

  return (
    <Drawer
      open={open}
      onClose={handleCloseDrawer}
      title={t("Transaction policies")}
      footer={
        <>
          <Button
            variant="primary-outlined"
            onClick={() => {
              handleCloseDrawer();
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={() => formik.handleSubmit()}
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack gap="var(--space-7)">
          <Stack gap="var(--space-3)">
            <Inline gap="var(--space-3)" wrap={false}>
              <ListItemIcon wrapper="brand" sx={{ minWidth: 0 }}>
                <HugeiconsIcon
                  icon={isReceiptPolicy ? Invoice03Icon : PolicyIcon}
                  size={16}
                />
              </ListItemIcon>
              <Typography variant="h5">{drawerDescription}</Typography>
            </Inline>
            <Typography variant="body1">
              {isReceiptPolicy
                ? t("Require users to submit receipt for transactions")
                : t("Require users to allocate costs for transactions")}
            </Typography>
          </Stack>

          <Stack gap="var(--space-3)">
            <TextField
              name="transactionsAmount"
              size="md"
              label={t("Transaction Amount")}
              helperText={
                isReceiptPolicy
                  ? t(
                      "Set the transaction amount at which users must provide a receipt",
                    )
                  : t(
                      "Set the transaction amount at which users must allocate costs",
                    )
              }
              fullWidth
              type="number"
              leading={
                <Typography component="span" variant="inherit">
                  {currencySymbol}
                </Typography>
              }
              onChange={formik.handleChange}
              value={formik.values["transactionsAmount"]}
              errorText={
                formik.errors["transactionsAmount"] &&
                formik.touched["transactionsAmount"]
                  ? (formik.errors["transactionsAmount"] as string)
                  : undefined
              }
              inputProps={{
                // Disable scroll wheel increment/decrement
                onWheel: (event: React.WheelEvent<HTMLInputElement>) =>
                  event.currentTarget.blur(),
              }}
            />
          </Stack>

          <RadioGroup
            row
            name="isAllCategories"
            value={formik.values.isAllCategories}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel
              value={"0"}
              control={<Radio size="small" />}
              label={t("settingPolicies.applySelectedCategory")}
            />
            <FormControlLabel
              value={"1"}
              control={<Radio size="small" />}
              label={t("settingPolicies.applyAllCategory")}
            />
          </RadioGroup>

          <Typography variant="caption">{t("noteSetSamePolicy")}</Typography>
        </Stack>
      </form>
    </Drawer>
  );
};

export default TransactionPoliciesDrawer;
